import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WaPlatformSecrets } from "@/components/whatsapp/WaPlatformSecrets";

const err = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };

function SendingEditor({ row, onSaved }) {
  const [nums, setNums] = useState((row.test_recipients || []).join(", "));
  const [live, setLive] = useState(row.live_enabled);
  const [tpl, setTpl] = useState(row.templates_enabled);
  const save = async () => {
    try {
      await api.put(`/wa/cloud/admin/companies/${row.company_id}/sending`, { live_enabled: live, templates_enabled: tpl, send_scope: "test_recipients", test_recipients: nums.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 pt-2" data-testid={`wa-admin-sending-${row.company_id}`}>
      <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} data-testid={`wa-admin-live-${row.company_id}`} />Allow sending (test recipients only)</label>
      <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={tpl} onChange={(e) => setTpl(e.target.checked)} data-testid={`wa-admin-templates-${row.company_id}`} />Allow template create/delete</label>
      <Input className="flex-1 min-w-[220px] h-8 text-xs" placeholder="+447700900101, +447700900102" value={nums} onChange={(e) => setNums(e.target.value)} data-testid={`wa-admin-recipients-${row.company_id}`} />
      <Button size="sm" variant="outline" onClick={save} data-testid={`wa-admin-save-${row.company_id}`}>Save</Button>
    </div>
  );
}

const Row = ({ r, onSaved }) => (
  <div className="py-3 space-y-1" data-testid={`wa-admin-row-${r.company_id}`}>
    <div className="flex flex-wrap items-center gap-2 text-sm"><b className="text-slate-900">{r.company}</b><span className="text-xs text-slate-500">{r.slug}</span>
      <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5" data-testid={`wa-admin-status-${r.company_id}`}>{r.status}</span>
      {r.alerts > 0 && <span className="text-xs rounded-full bg-rose-100 text-rose-700 px-2 py-0.5">{r.alerts} alert(s)</span>}</div>
    <p className="text-xs text-slate-600">{r.number || "No number"} · method {r.method || "—"} · WABA {r.waba_id || "—"} · phone ID {r.phone_number_id || "—"} · quality {r.quality || "—"}</p>
    <p className="text-xs text-slate-500">Permissions: {r.scopes.join(", ") || "—"} · health {r.health || "—"}{r.last_health_at ? ` (${formatDateTime(r.last_health_at)})` : ""} · last webhook {r.last_webhook_at ? formatDateTime(r.last_webhook_at) : "never"}</p>
    <SendingEditor row={r} onSaved={onSaved} />
  </div>
);

export default function PlatformWhatsApp() {
  const [d, setD] = useState(null);
  const load = () => api.get("/wa/cloud/admin/overview").then((r) => setD(r.data)).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="p-6 text-sm text-slate-400">Loading…</p>;
  const p = d.platform;
  return (
    <div className="p-6 space-y-4 max-w-5xl" data-testid="platform-whatsapp-page">
      <div className="flex items-center gap-2"><MessageCircle className="w-6 h-6 text-emerald-600" /><h1 className="text-2xl font-bold text-white">WhatsApp connections</h1></div>
      <div className="text-xs text-slate-600 bg-white border rounded-xl p-3 space-y-0.5" data-testid="wa-admin-platform">
        <p>Meta app configured: <b>{p.ready ? "Yes" : "No"}</b> · Global sending switch: <b>{p.global_live ? "On" : "Off (all sending locked)"}</b></p>
        <p>Companies approved to connect: {p.connect_allowed.join(", ") || "none"} · approved to send: {p.send_allowed.join(", ") || "none"}</p>
        <p data-testid="wa-admin-webhook-health">Webhook last 24h: <b className={p.bad_signatures_24h ? "text-rose-700" : ""}>{p.bad_signatures_24h} rejected signatures</b> · {p.unknown_routes_24h} events for unconnected numbers (ignored)</p>
        <p>A company can only send when the global switch is on, it is approved to send, sending is ticked below, and the recipient is on its test list.</p></div>
      <WaPlatformSecrets />
      <section className="bg-white text-slate-800 border rounded-xl px-4 divide-y" data-testid="wa-admin-list">
        {d.connections.map((r) => <Row key={r.company_id} r={r} onSaved={load} />)}
        {!d.connections.length && <p className="py-4 text-sm text-slate-400">No WhatsApp connections yet.</p>}</section>
    </div>
  );
}
